import { $ } from '../core/TsDOM'

/**
 * Banner Carousel Component
 * A native, performant carousel for hero banners with autoplay and navigation
 */
class BannerCarousel extends HTMLElement {
	private currentIndex = 0
	private slides: HTMLElement[] = []
	private dots: HTMLButtonElement[] = []
	private controls: HTMLElement | null = null
	private controlsParent: HTMLElement | null = null
	private handleResize: (() => void) | null = null
	private autoplayInterval: number | null = null
	private isAutoplayEnabled = false
	private autoplaySpeed = 5000
	private isTransitioning = false
	private pauseOnHover = true
	private transitionStyle = 'fade'
	private editorStorageKey: string | null = null
	private editorBlockStorageKey: string | null = null
	private isInEditor = false
	private activeVideo: HTMLVideoElement | null = null
	private activeVideoEndHandler: (() => void) | null = null
	private activeVideoTimeHandler: (() => void) | null = null
	private activeVideoLastTime = 0
	private activeVideoLoopHandled = false

	connectedCallback() {
		this.init()
	}

	disconnectedCallback() {
		this.stopAutoplay()
		if (this.handleResize) {
			window.removeEventListener('resize', this.handleResize)
		}
	}

	private init() {
		// Get configuration from data attributes
		this.isAutoplayEnabled = this.dataset.autoplay === 'true'
		this.autoplaySpeed = parseInt(this.dataset.speed || '5', 10) * 1000
		this.pauseOnHover = this.dataset.pauseOnHover !== 'false'
		this.transitionStyle = this.dataset.transition || 'fade'

		// Disable autoplay in Shopify theme editor for better editing experience
		this.isInEditor = typeof window !== 'undefined' &&
			(window as any).Shopify?.designMode === true
		if (this.isInEditor) {
			this.isAutoplayEnabled = false
		}

		// Get all slides
		this.slides = Array.from(
			this.querySelectorAll('.banner-carousel__slide')
		) as HTMLElement[]

		// Get all pagination dots
		this.dots = Array.from(
			this.querySelectorAll('.banner-carousel__dot')
		) as HTMLButtonElement[]

		this.controls = this.querySelector('.banner-carousel__controls')
		this.controlsParent = this.controls?.parentElement ?? null

		// Setup event listeners
		this.setupNavigation()
		this.setupTouchGestures()
		this.setupEditorPersistence()
		this.setupControlsPlacement()

		// Start autoplay if enabled (and not in editor)
		if (this.isAutoplayEnabled && this.slides.length > 1) {
			this.startAutoplay()
		}

		// Pause autoplay on hover (desktop only) if enabled
		if (this.pauseOnHover) {
			this.addEventListener('mouseenter', () => this.stopAutoplay())
			this.addEventListener('mouseleave', () => {
				if (this.isAutoplayEnabled) {
					this.startAutoplay()
				}
			})
		}

		// Pause autoplay when user interacts
		this.addEventListener('touchstart', () => this.stopAutoplay())
	}

	private setupNavigation() {
		// Dot navigation
		this.dots.forEach((dot, index) => {
			dot.addEventListener('click', () => {
				this.goToSlide(index)
			})
		})

		// Arrow navigation
		const prevButton = this.querySelector('.banner-carousel__arrow--prev')
		const nextButton = this.querySelector('.banner-carousel__arrow--next')

		if (prevButton) {
			prevButton.addEventListener('click', () => this.previousSlide())
		}

		if (nextButton) {
			nextButton.addEventListener('click', () => this.nextSlide())
		}

		// Keyboard navigation
		this.addEventListener('keydown', (e) => {
			if (e.key === 'ArrowLeft') {
				this.previousSlide()
			} else if (e.key === 'ArrowRight') {
				this.nextSlide()
			}
		})
	}

	private setupEditorPersistence() {
		if (!this.isInEditor) return

		const sectionId = this.dataset.sectionId
		if (!sectionId) return

		this.editorStorageKey = `banner-carousel:${sectionId}`
		this.editorBlockStorageKey = `banner-carousel:block:${sectionId}`
		const storedIndex = window.sessionStorage.getItem(this.editorStorageKey)
		const parsedIndex = storedIndex ? parseInt(storedIndex, 10) : NaN
		if (!Number.isNaN(parsedIndex) && this.slides[parsedIndex]) {
			this.goToSlide(parsedIndex)
		}

		this.restoreEditorBlockSelection()

		document.addEventListener('shopify:block:select', (event: Event) => {
			const customEvent = event as CustomEvent
			const detailBlockId = customEvent.detail?.blockId as string | undefined
			const target = event.target as HTMLElement | null

			const slide = target?.closest('.banner-carousel__slide') as
				| HTMLElement
				| null
			if (!slide) return

			const slideIndex = parseInt(slide.dataset.slideIndex || '', 10)
			if (!Number.isNaN(slideIndex)) {
				this.goToSlide(slideIndex)
			}

			const blockId = detailBlockId || slide.dataset.blockId
			if (blockId && this.editorBlockStorageKey) {
				window.sessionStorage.setItem(this.editorBlockStorageKey, blockId)
			}
		})
	}

	private setupControlsPlacement() {
		this.handleResize = () => this.updateControlsPlacement()
		window.addEventListener('resize', this.handleResize)
		this.updateControlsPlacement()
	}

	private updateControlsPlacement() {
		if (!this.controls) return

		const isMobile = window.matchMedia('(max-width: 749px)').matches
		const activeSlide = this.slides[this.currentIndex]
		const mediaTarget = activeSlide?.querySelector('.banner-carousel__media') as HTMLElement | null
		const contentTarget = activeSlide?.querySelector(
			'.banner-carousel__content-mobile'
		) as HTMLElement | null

		if (isMobile) {
			const mobileLayout = activeSlide?.dataset.mobileContentLayout
			const mobileTarget = mobileLayout === 'overlay' ? contentTarget : mediaTarget
			if (mobileTarget && this.controls.parentElement !== mobileTarget) {
				mobileTarget.appendChild(this.controls)
			}
			return
		}

		if (this.controlsParent && this.controls.parentElement !== this.controlsParent) {
			this.controlsParent.appendChild(this.controls)
		}
	}

	private getVisibleVideo(slide: HTMLElement): HTMLVideoElement | null {
		const videos = Array.from(slide.querySelectorAll('video')) as HTMLVideoElement[]
		if (!videos.length) return null

		const visible = videos.find((video) => {
			const rect = video.getBoundingClientRect()
			return rect.width > 0 && rect.height > 0
		})

		return visible || videos[0]
	}

	private clearActiveVideoHandler() {
		if (this.activeVideo) {
			if (this.activeVideoEndHandler) {
				this.activeVideo.removeEventListener('ended', this.activeVideoEndHandler)
			}
			if (this.activeVideoTimeHandler) {
				this.activeVideo.removeEventListener('timeupdate', this.activeVideoTimeHandler)
			}
		}
		this.activeVideo = null
		this.activeVideoEndHandler = null
		this.activeVideoTimeHandler = null
		this.activeVideoLastTime = 0
		this.activeVideoLoopHandled = false
	}

	private stopSlideVideos(slide: HTMLElement | null) {
		if (!slide) return
		const videos = Array.from(slide.querySelectorAll('video')) as HTMLVideoElement[]
		videos.forEach((video) => {
			video.pause()
			try {
				video.currentTime = 0
			} catch {
				// Ignore if the video cannot seek yet.
			}
		})
	}

	private configureAutoplayForActiveSlide() {
		if (!this.isAutoplayEnabled || this.slides.length <= 1 || this.isInEditor) {
			return
		}

		this.stopAutoplayInterval()
		this.clearActiveVideoHandler()

		const activeSlide = this.slides[this.currentIndex]
		if (!activeSlide) return

		const shouldPlayFull = activeSlide.dataset.videoPlayFull === 'true'
		if (shouldPlayFull) {
			const video = this.getVisibleVideo(activeSlide)
			if (video) {
				this.activeVideo = video
				this.activeVideo.currentTime = 0
				this.activeVideoLastTime = 0
				this.activeVideoLoopHandled = false
				const handleVideoEnd = () => {
					if (this.activeVideoLoopHandled) return
					this.activeVideoLoopHandled = true
					this.clearActiveVideoHandler()
					this.nextSlide()
				}
				const handleVideoTimeUpdate = () => {
					if (!this.activeVideo || this.activeVideoLoopHandled) return
					const duration = this.activeVideo.duration
					if (!Number.isFinite(duration) || duration <= 0) {
						this.activeVideoLastTime = this.activeVideo.currentTime
						return
					}
					const currentTime = this.activeVideo.currentTime
					if (this.activeVideoLastTime > 0.1 && currentTime < this.activeVideoLastTime - 0.1) {
						this.activeVideoLoopHandled = true
						this.clearActiveVideoHandler()
						this.nextSlide()
						return
					}
					this.activeVideoLastTime = currentTime
				}
				this.activeVideoEndHandler = handleVideoEnd
				this.activeVideoTimeHandler = handleVideoTimeUpdate
				video.addEventListener('ended', handleVideoEnd, { once: true })
				video.addEventListener('timeupdate', handleVideoTimeUpdate)
				const playPromise = video.play()
				if (playPromise && typeof playPromise.catch === 'function') {
					playPromise.catch(() => undefined)
				}
				return
			}
		}

		this.startAutoplayInterval()
	}

	private restoreEditorBlockSelection() {
		if (!this.editorBlockStorageKey) return

		const blockId = window.sessionStorage.getItem(this.editorBlockStorageKey)
		if (!blockId) return

		const block = this.querySelector<HTMLElement>(
			`[data-block-id="${blockId}"]`
		)
		if (!block) return

		window.setTimeout(() => {
			block.scrollIntoView({ block: 'nearest', inline: 'nearest' })
			block.dispatchEvent(
				new MouseEvent('click', { bubbles: true, cancelable: true })
			)
		}, 50)
	}

	private setupTouchGestures() {
		let touchStartX = 0
		let touchEndX = 0

		this.addEventListener('touchstart', (e) => {
			touchStartX = e.touches[0].clientX
		})

		this.addEventListener('touchend', (e) => {
			touchEndX = e.changedTouches[0].clientX
			this.handleSwipe(touchStartX, touchEndX)
		})
	}

	private handleSwipe(startX: number, endX: number) {
		const swipeThreshold = 50
		const diff = startX - endX

		if (Math.abs(diff) > swipeThreshold) {
			if (diff > 0) {
				// Swiped left, go to next
				this.nextSlide()
			} else {
				// Swiped right, go to previous
				this.previousSlide()
			}
		}
	}

	private goToSlide(index: number) {
		if (this.isTransitioning || index === this.currentIndex) {
			return
		}

		this.isTransitioning = true
		const prevSlide = this.slides[this.currentIndex]
		this.stopSlideVideos(prevSlide)

		// Remove active class from current slide and dot
		this.slides[this.currentIndex]?.classList.remove('active')
		this.dots[this.currentIndex]?.classList.remove('active')

		// Update current index
		this.currentIndex = index

		// Add active class to new slide and dot
		this.slides[this.currentIndex]?.classList.add('active')
		this.dots[this.currentIndex]?.classList.add('active')
		this.updateControlsPlacement()
		this.configureAutoplayForActiveSlide()

		if (this.editorStorageKey) {
			window.sessionStorage.setItem(this.editorStorageKey, `${index}`)
		}

		// Allow transition after animation completes
		setTimeout(() => {
			this.isTransitioning = false
		}, 500)

		// Reset autoplay timer
		if (this.isAutoplayEnabled) {
			this.startAutoplay()
		}
	}

	private nextSlide() {
		const nextIndex = (this.currentIndex + 1) % this.slides.length
		this.goToSlide(nextIndex)
	}

	private previousSlide() {
		const prevIndex =
			this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1
		this.goToSlide(prevIndex)
	}

	private startAutoplay() {
		this.stopAutoplayInterval()
		this.configureAutoplayForActiveSlide()
	}

	private stopAutoplay() {
		this.stopAutoplayInterval()
		this.clearActiveVideoHandler()
	}

	private startAutoplayInterval() {
		if (this.slides.length > 1) {
			this.autoplayInterval = window.setInterval(() => {
				this.nextSlide()
			}, this.autoplaySpeed)
		}
	}

	private stopAutoplayInterval() {
		if (this.autoplayInterval) {
			clearInterval(this.autoplayInterval)
			this.autoplayInterval = null
		}
	}
}

// Register the custom element
if (!customElements.get('banner-carousel-component')) {
	customElements.define('banner-carousel-component', BannerCarousel)
}

export default BannerCarousel
